import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Clock, ExternalLink } from 'lucide-react';

interface Video {
  title: string;
  url: string;
  embed_url: string;
  duration: string;
  channel: string;
  date: string;
  video_id: string;
  snippet: string;
}

export const VideoSection = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load video data from the JSON file
    fetch('/video/video_search.json')
      .then(response => response.json())
      .then(data => {
        setVideos(data.videos || []);
        if (data.videos && data.videos.length > 0) {
          setSelectedVideo(data.videos[0]);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading videos:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <section id="videos" className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="videos" className="py-20 px-4 bg-gray-50">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Course <span className="text-blue-600">Videos</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            High-quality video lectures from industry experts and top educational channels
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Video Player */}
          <div className="lg:col-span-2">
            {selectedVideo && (
              <Card className="overflow-hidden shadow-lg">
                <div className="aspect-video">
                  <iframe
                    src={selectedVideo.embed_url}
                    title={selectedVideo.title}
                    className="w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2">{selectedVideo.title}</h3>
                  <p className="text-muted-foreground mb-4">{selectedVideo.snippet}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Badge variant="secondary">{selectedVideo.channel}</Badge>
                      <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{selectedVideo.duration}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={selectedVideo.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Watch on YouTube
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Video Playlist */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Course Playlist</h3>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {videos.map((video, index) => (
                <Card 
                  key={video.video_id}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedVideo?.video_id === video.video_id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedVideo(video)}
                >
                  <CardContent className="p-4">
                    <div className="flex space-x-3">
                      <div className="relative flex-shrink-0">
                        <img
                          src={`https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`}
                          alt={video.title}
                          className="w-20 h-14 object-cover rounded"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="h-6 w-6 text-white drop-shadow-lg" />
                        </div>
                        <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1 rounded">
                          {video.duration}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm leading-tight mb-1 line-clamp-2">
                          {video.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mb-1">{video.channel}</p>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline" className="text-xs">
                            Lesson {index + 1}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Video Features */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <Card className="text-center p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="font-semibold mb-2">HD Quality Videos</h3>
            <p className="text-sm text-muted-foreground">
              All videos are available in high definition for the best learning experience
            </p>
          </Card>
          
          <Card className="text-center p-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="font-semibold mb-2">Self-Paced Learning</h3>
            <p className="text-sm text-muted-foreground">
              Learn at your own pace with lifetime access to all course materials
            </p>
          </Card>
          
          <Card className="text-center p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExternalLink className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="font-semibold mb-2">Expert Instructors</h3>
            <p className="text-sm text-muted-foreground">
              Learn from industry experts and top educational channels worldwide
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};